import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Section } from '../shared';

// Floating Particles Background
const FloatingParticles = () => {
  const particles = Array.from({ length: 30 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 4 + 2,
    duration: Math.random() * 10 + 10,
    delay: Math.random() * 5,
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute rounded-full bg-neon-orange/20"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.size,
            height: p.size,
          }}
          animate={{
            y: [0, -30, 0],
            opacity: [0.2, 0.6, 0.2],
            scale: [1, 1.5, 1],
          }}
          transition={{
            duration: p.duration,
            repeat: Infinity,
            delay: p.delay,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
};

// Animated Sound Wave
const SoundWave = ({ isActive, color = "#FD802E" }) => {
  const bars = 12;
  return (
    <div className="flex items-center justify-center gap-0.5 h-8">
      {Array.from({ length: bars }).map((_, i) => (
        <motion.div
          key={i}
          className="w-1 rounded-full"
          style={{ backgroundColor: color }}
          animate={{
            height: isActive 
              ? [4, 20 + Math.random() * 12, 4] 
              : 4,
          }}
          transition={{
            duration: 0.4,
            repeat: isActive ? Infinity : 0,
            delay: i * 0.05,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
};

// 3D Floating Step Card
const StepCard = ({ step, index, isActive, onClick, totalSteps }) => {
  const [isHovered, setIsHovered] = useState(false);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  const icons = {
    upload: '/How%20It%20Works.webp',
    analyze: '/How%20It%20Works%20(2).webp',
    process: '/How%20It%20Works%20(3).webp',
    export: '/How%20It%20Works%20(4).webp',
  };

  return (
    <motion.div
      ref={ref}
      className="relative"
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.15 }}
    >
      {/* Connection Line to Next Step */}
      {index < totalSteps - 1 && (
        <div className="hidden lg:block absolute top-1/2 -right-8 w-16 h-0.5 z-0">
          <motion.div
            className="h-full bg-gradient-to-r from-neon-orange to-transparent"
            initial={{ scaleX: 0 }}
            animate={isInView ? { scaleX: 1 } : {}}
            transition={{ duration: 0.8, delay: index * 0.15 + 0.5 }}
            style={{ transformOrigin: 'left' }}
          />
          {/* Animated dot traveling along the line */}
          <motion.div
            className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-neon-orange"
            animate={{ 
              x: [0, 60, 0],
              opacity: [1, 0.5, 1],
            }}
            transition={{ 
              duration: 2, 
              repeat: Infinity, 
              delay: index * 0.5,
              ease: "easeInOut" 
            }}
          />
        </div>
      )}

      {/* The Card */}
      <motion.div
        className="relative cursor-pointer"
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        onClick={() => onClick(index)}
        whileHover={{ y: -10, scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        {/* Glow Effect */}
        <motion.div
          className="absolute -inset-1 rounded-3xl bg-gradient-to-r from-neon-orange/50 to-neon-orange-light/50 blur-xl"
          animate={{
            opacity: isActive || isHovered ? 0.6 : 0.2,
            scale: isActive || isHovered ? 1.1 : 1,
          }}
          transition={{ duration: 0.3 }}
        />

        {/* Card Body */}
        <div 
          className={`relative bg-dark-deeper/90 backdrop-blur-xl rounded-3xl p-6 lg:p-8 border-2 transition-all duration-300 ${
            isActive 
              ? 'border-neon-orange shadow-[0_0_40px_rgba(253,128,46,0.3)]' 
              : 'border-neon-orange/20 hover:border-neon-orange/50'
          }`}
          style={{
            minWidth: '280px',
            maxWidth: '320px',
          }}
        >
          {/* Step Number Badge */}
          <motion.div
            className="absolute -top-4 -right-4 w-12 h-12 rounded-full bg-dark-base border-2 border-neon-orange flex items-center justify-center"
            animate={{
              boxShadow: isActive 
                ? ['0 0 10px #FD802E', '0 0 30px #FD802E', '0 0 10px #FD802E']
                : '0 0 10px rgba(253,128,46,0.3)',
            }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <span className="text-neon-orange font-heading font-bold">{step.number}</span>
          </motion.div>

          {/* Icon Container */}
          <motion.div
            className="w-16 h-16 rounded-2xl bg-gradient-to-br from-neon-orange/20 to-neon-orange/5 flex items-center justify-center mb-5 border border-neon-orange/30"
            animate={{
              rotate: isActive ? [0, 5, -5, 0] : 0,
            }}
            transition={{ duration: 0.5, repeat: isActive ? Infinity : 0, repeatDelay: 1 }}
          >
            <span className="text-neon-orange">
              {typeof icons[step.iconType] === 'string' ? (
                <img src={icons[step.iconType]} alt={step.title} className="w-12 h-12 object-contain" />
              ) : (
                icons[step.iconType]
              )}
            </span>
          </motion.div>

          {/* Title */}
          <h3 className="text-xl font-heading font-bold text-white mb-3">{step.title}</h3>

          {/* Description */}
          <p className="text-gray-400 text-sm leading-relaxed mb-5">{step.description}</p>

          {/* Sound Wave Visualizer */}
          <div className="pt-4 border-t border-neon-orange/10">
            <SoundWave isActive={isActive} />
            <motion.p
              className="text-center text-xs mt-2"
              animate={{ color: isActive ? '#FD802E' : '#6B7280' }}
            >
              {isActive ? '♪ Playing preview...' : 'Click to preview'}
            </motion.p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Mobile Step Card
const MobileStepCard = ({ step, index, isActive, onClick, totalSteps }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-30px" });

  const icons = {
    upload: '/How%20It%20Works.webp',
    analyze: '/How%20It%20Works%20(2).webp',
    process: '/How%20It%20Works%20(3).webp',
    export: '/How%20It%20Works%20(4).webp',
  };

  return (
    <motion.div
      ref={ref}
      className="relative"
      initial={{ opacity: 0, x: -30 }}
      animate={isInView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <div className="flex gap-4">
        {/* Timeline */}
        <div className="flex flex-col items-center">
          <motion.div
            className={`w-12 h-12 rounded-full flex items-center justify-center border-2 ${
              isActive ? 'border-neon-orange bg-neon-orange/20' : 'border-neon-orange/30 bg-dark-deeper'
            }`}
            animate={{
              boxShadow: isActive ? '0 0 20px rgba(253,128,46,0.5)' : 'none',
            }}
            onClick={() => onClick(index)}
          >
            <span className="text-neon-orange font-heading font-bold text-sm">{step.number}</span>
          </motion.div>
          {index < totalSteps - 1 && (
            <motion.div
              className="w-0.5 flex-1 bg-neon-orange/30 my-2"
              initial={{ scaleY: 0 }}
              animate={isInView ? { scaleY: 1 } : {}}
              transition={{ duration: 0.5, delay: 0.3 }}
              style={{ transformOrigin: 'top', minHeight: '60px' }}
            />
          )}
        </div>

        {/* Content */}
        <motion.div
          className={`flex-1 pb-8 bg-dark-deeper/50 rounded-2xl p-4 border ${
            isActive ? 'border-neon-orange/50' : 'border-neon-orange/10'
          }`}
          onClick={() => onClick(index)}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-neon-orange/10 flex items-center justify-center text-neon-orange">
              {typeof icons[step.iconType] === 'string' ? (
                <img src={icons[step.iconType]} alt={step.title} className="w-8 h-8 object-contain" />
              ) : (
                icons[step.iconType]
              )}
            </div>
            <h3 className="text-lg font-heading font-bold text-white">{step.title}</h3>
          </div>
          <p className="text-gray-400 text-sm mb-3">{step.description}</p>
          <SoundWave isActive={isActive} />
        </motion.div>
      </div>
    </motion.div>
  );
};

// Circular Progress Indicator
const CircularProgress = ({ activeStep, totalSteps }) => {
  const radius = 80;
  const circumference = 2 * Math.PI * radius;
  const progress = ((activeStep + 1) / totalSteps) * circumference;

  return (
    <div className="relative w-48 h-48 mx-auto mb-12">
      {/* Background circle */}
      <svg className="w-full h-full -rotate-90">
        <circle
          cx="96"
          cy="96"
          r={radius}
          fill="none"
          stroke="rgba(253, 128, 46, 0.1)"
          strokeWidth="4"
        />
        <motion.circle
          cx="96"
          cy="96"
          r={radius}
          fill="none"
          stroke="#FD802E"
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: circumference - progress }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          style={{
            filter: 'drop-shadow(0 0 10px rgba(253,128,46,0.5))',
          }}
        />
      </svg>

      {/* Center content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <motion.span
          key={activeStep}
          className="text-4xl font-heading font-bold text-neon-orange"
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          {activeStep + 1}/{totalSteps}
        </motion.span>
        <span className="text-gray-400 text-sm mt-1">Steps</span>
      </div>
    </div>
  );
};

const HowItWorksSection = () => {
  const [activeStep, setActiveStep] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const navigate = useNavigate();

  // Auto-advance steps
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 4);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const steps = [
    {
      number: "01",
      title: "Upload Your Audio",
      description: "Drag and drop your audio files or record directly. We support WAV, MP3, FLAC, and 20+ formats.",
      iconType: "upload",
    },
    {
      number: "02",
      title: "AI Analysis",
      description: "Our AI analyzes patterns, quality issues, and optimization opportunities in real-time.",
      iconType: "analyze",
    },
    {
      number: "03",
      title: "Smart Processing",
      description: "Apply AI-powered enhancements, generate content, or automate workflow tasks intelligently.",
      iconType: "process",
    },
    {
      number: "04",
      title: "Export & Share",
      description: "Download your optimized audio in any format or share directly to your favorite platforms.",
      iconType: "export",
    },
  ];

  const handleStepClick = (index) => {
    setActiveStep(index);
  };

  return (
    <Section id="how-it-works" className="bg-dark-deeper relative overflow-hidden">
      {/* Background Effects */}
      <FloatingParticles />
      
      {/* Gradient Orbs */}
      <motion.div
        className="absolute top-1/4 -left-32 w-96 h-96 bg-neon-orange/10 rounded-full blur-3xl"
        animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-1/4 -right-32 w-96 h-96 bg-neon-orange/5 rounded-full blur-3xl"
        animate={{ scale: [1.2, 1, 1.2], opacity: [0.2, 0.4, 0.2] }}
        transition={{ duration: 10, repeat: Infinity }}
      />

      <div className="max-w-7xl mx-auto relative z-10" ref={ref}>
        {/* Section Header */}
        <div className="text-center mb-16">
          <motion.span
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-neon-orange/10 text-neon-orange text-sm font-medium mb-4 border border-neon-orange/20"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
          >
            <span className="w-2 h-2 rounded-full bg-neon-orange animate-pulse" />
            How It Works
          </motion.span>

          <motion.h2
            className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.1 }}
          >
            Your Audio Journey in{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-orange to-neon-orange-light" style={{ textShadow: '0 0 40px rgba(253,128,46,0.3)' }}>
              4 Simple Steps
            </span>
          </motion.h2>

          <motion.p
            className="text-gray-400 text-lg max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
          >
            From upload to export, experience the future of audio processing. Click any step to hear a preview.
          </motion.p>
        </div>

        {/* Circular Progress - Desktop Only */}
        <div className="hidden lg:block">
          <CircularProgress activeStep={activeStep} totalSteps={steps.length} />
        </div>

        {/* Desktop: Horizontal Cards */}
        <div className="hidden lg:flex justify-center items-start gap-8 flex-wrap">
          {steps.map((step, index) => (
            <StepCard
              key={index}
              step={step}
              index={index}
              isActive={activeStep === index}
              onClick={handleStepClick}
              totalSteps={steps.length}
            />
          ))}
        </div>

        {/* Mobile: Vertical Timeline */}
        <div className="lg:hidden px-4">
          {steps.map((step, index) => (
            <MobileStepCard
              key={index}
              step={step}
              index={index}
              isActive={activeStep === index}
              onClick={handleStepClick}
              totalSteps={steps.length}
            />
          ))}
        </div>

        {/* Step Navigation Dots - Mobile */}
        <div className="flex lg:hidden justify-center gap-3 mt-8">
          {steps.map((_, index) => (
            <motion.button
              key={index}
              className={`w-3 h-3 rounded-full ${
                activeStep === index ? 'bg-neon-orange' : 'bg-neon-orange/30'
              }`}
              onClick={() => handleStepClick(index)}
              whileHover={{ scale: 1.3 }}
              whileTap={{ scale: 0.9 }}
              animate={{
                boxShadow: activeStep === index ? '0 0 15px rgba(253,128,46,0.6)' : 'none',
              }}
            />
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6 }}
        >
          <motion.button
            className="px-8 py-4 bg-gradient-to-r from-neon-orange to-neon-orange-light text-dark-base font-bold rounded-full text-lg inline-flex items-center gap-3"
            onClick={() => { navigate('/veritonetech-ai'); }}
            aria-label="Start Your Audio Journey - View product page"
            whileHover={{ scale: 1.05, boxShadow: '0 0 40px rgba(253,128,46,0.5)' }}
            whileTap={{ scale: 0.95 }}
          >
            Start Your Audio Journey
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </motion.button>
        </motion.div>
      </div>
    </Section>
  );
};

export default HowItWorksSection;
